import styled from "styled-components";
import { Checkbox } from "rsuite";
import { AlertsContext } from "../_context/AlertsContext";
import { useContext, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axiosInstance from "../../../../../../appHelpers/axiosInstance";
import { InputContext } from "../../../_context/InputContext";
import RemindFillIcon from "@rsuite/icons/RemindFill";
import { colors } from "../../../../../ui/theme/colors";
import { shadows } from "../../../../../ui/theme/shadows";
import ArrowDownLineIcon from "@rsuite/icons/ArrowDownLine";
import ArrowUpLineIcon from "@rsuite/icons/ArrowUpLine";

export const AlertListFC = ({ user, system }) => {
  const PREVIEW_COUNT = 2; // Number of alerts to show in preview
  const [isExpanded, setIsExpanded] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const { alertConfigs, defaultAlertConfigs, setAlertConfigs } =
    useContext(AlertsContext);
  const { viewToggle } = useContext(InputContext);

  const onClickEnabled = async (alert) => {
    const updatedAlertConfigs = alertConfigs.map((config) => {
      if (config.id === alert.id) {
        return {
          ...config,
          enabled: !config.enabled,
        };
      }
      return config;
    });

    // Call a function to update the alertConfigs state
    setAlertConfigs(updatedAlertConfigs);

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "POST",
        url: `/alert/toggle/individual/model/`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          sme: system.id,
          model_id: alert.id,
        }),
      });
    } catch (error) {
      console.error(error);
    }
  };

  // Filter alerts based on view toggle
  const filteredAlerts = user
    ? alertConfigs?.filter(
        (config) =>
          config.system_id === system.id &&
          (viewToggle === "MMB"
            ? !config.pg_table.toLowerCase().includes("log") &&
              !config.pg_table.toLowerCase().includes("hhm")
            : viewToggle === "EDU"
              ? config.pg_table.toLowerCase().includes("edu")
              : viewToggle === "HHM"
                ? config.pg_table.toLowerCase().includes("log") ||
                  config.pg_table.toLowerCase().includes("hhm")
                : true),
      ) || []
    : [];

  const totalAlerts = filteredAlerts.length;
  const displayedAlerts = isExpanded
    ? filteredAlerts
    : filteredAlerts.slice(0, PREVIEW_COUNT);
  const hiddenCount = totalAlerts - PREVIEW_COUNT;

  return (
    <AlertContainer>
      <AlertHeader>
        <HeaderTitle>
          Your Currently Set{" "}
          {viewToggle === "MMB"
            ? "Magnet"
            : viewToggle === "EDU"
              ? "EDU"
              : viewToggle === "HHM"
                ? "Log"
                : ""}{" "}
          Alerts:
        </HeaderTitle>
        <HeaderInfo>
          <AlertCount>
            {totalAlerts} Alert{totalAlerts !== 1 ? "s" : ""}
          </AlertCount>
          <HeaderLabel>Enabled</HeaderLabel>
        </HeaderInfo>
      </AlertHeader>
      <AlertsList>
        {displayedAlerts.map((alert, index) => (
          <AlertItem
            key={`${index}_${alert.id}`}
            isDisabled={
              !defaultAlertConfigs.find(
                (dConfig) => dConfig.id === alert.default_id,
              )?.enabled
            }
          >
            {/* Shows Globally Disabled Banner if Default is Disabled */}
            {!defaultAlertConfigs.find(
              (dConfig) => dConfig.id === alert.default_id,
            )?.enabled ? (
              <DisabledBanner>
                <RemindFillIcon /> This alert has been globally disabled!
              </DisabledBanner>
            ) : null}

            <AlertContent>
              <SeverityBadge severity={alert.severity}>
                {alert.severity}
              </SeverityBadge>

              {viewToggle !== "HHM" ? (
                <AlertDetails>
                  <AlertField>{alert.field_name_alias}</AlertField>
                  <AlertTrigger>
                    {alert.field_name === "comp_vib_status" ? (
                      <>Trigger: {alert.field_name_alias} is OFF!</>
                    ) : alert.operator === "absent_null_data" ? (
                      `Trigger: ${alert?.threshold} count(s) of ${alert?.field_name_alias}.`
                    ) : alert.operator?.toLowerCase() === "offline" ||
                      alert.operator?.toLowerCase() === "frozen_host" ? (
                      <>
                        Trigger: {alert.field_name_alias} within past{" "}
                        {alert.threshold} {alert.threshold_units}.
                      </>
                    ) : (
                      <>
                        Trigger: {alert.field_name_alias} is{" "}
                        {alert.operator?.replace(/_/g, " ")} {alert.threshold}{" "}
                        {alert.threshold_units}.
                      </>
                    )}
                  </AlertTrigger>
                </AlertDetails>
              ) : (
                <AlertDetails>
                  <AlertTrigger>
                    {alert.operator?.toLowerCase() === "offline" ||
                    alert.operator?.toLowerCase() === "frozen_host" ? (
                      <>
                        Trigger: {alert.field_name_alias} within past{" "}
                        {alert.threshold} {alert.threshold_units}.
                      </>
                    ) : (
                      <>{alert.field_name_alias}</>
                    )}
                  </AlertTrigger>
                </AlertDetails>
              )}

              <CheckboxWrapper>
                <Checkbox
                  value={alert.enabled}
                  checked={alert.enabled}
                  disabled={
                    !defaultAlertConfigs.find(
                      (dConfig) => dConfig.id === alert.default_id,
                    )?.enabled
                  }
                  onChange={() => onClickEnabled(alert)}
                />
              </CheckboxWrapper>
            </AlertContent>
          </AlertItem>
        ))}
      </AlertsList>

      {totalAlerts > PREVIEW_COUNT && (
        <ExpandButton onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? (
            <>
              <ArrowUpLineIcon /> Show Less
            </>
          ) : (
            <>
              <ArrowDownLineIcon /> Show {hiddenCount} More Alert
              {hiddenCount !== 1 ? "s" : ""}
            </>
          )}
        </ExpandButton>
      )}
    </AlertContainer>
  );
};

const AlertContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const AlertHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  border-radius: 16px 16px 0 0;
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 2px;
`;

const HeaderTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.neutral[700]};
  margin: 0;
`;

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AlertCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.primary.main};
  background: rgba(0, 91, 148, 0.1);
  padding: 4px 10px;
  border-radius: 12px;
  border: 1px solid rgba(0, 91, 148, 0.2);
`;

const HeaderLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.neutral[600]};
`;

const AlertsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AlertItem = styled.div`
  background: ${(props) =>
    props.isDisabled ? "rgba(229, 229, 234, 0.3)" : "rgba(255, 255, 255, 0.1)"};
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 16px 20px;
  min-height: ${(props) => (props.isDisabled ? "80px" : "60px")};
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) =>
      props.isDisabled
        ? "rgba(229, 229, 234, 0.4)"
        : "rgba(255, 255, 255, 0.15)"};
    transform: translateY(-1px);
    box-shadow: ${shadows.md};
  }
`;

const DisabledBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(245, 166, 35, 0.1);
  border: 1px solid rgba(245, 166, 35, 0.3);
  border-radius: 8px;
  color: ${colors.warning.main};
  font-size: 13px;
  font-weight: 500;
`;

const AlertContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const SeverityBadge = styled.div`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  min-width: 70px;
  text-align: center;
  color: ${(props) =>
    props.severity === "high"
      ? colors.error.main
      : props.severity === "medium"
        ? colors.warning.main
        : colors.neutral[600]};
  background: ${(props) =>
    props.severity === "high"
      ? colors.error.light
      : props.severity === "medium"
        ? colors.warning.light
        : colors.neutral[200]};
  border: 1px solid
    ${(props) =>
      props.severity === "high"
        ? colors.error.border
        : props.severity === "medium"
          ? colors.warning.border
          : colors.neutral[300]};
`;

const AlertDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const AlertField = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.neutral[700]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AlertTrigger = styled.div`
  font-size: 13px;
  color: ${colors.neutral[600]};
  line-height: 1.4;
  word-wrap: break-word;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
`;

const ExpandButton = styled.button`
  width: 100%;
  padding: 12px 20px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0 0 16px 16px;
  border-top: none;
  color: ${colors.primary.main};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    transform: translateY(-1px);
    box-shadow: ${shadows.sm};
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 16px;
  }
`;
